import {
  Box,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { alpha, useTheme } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import type { GetTransactionDetailResponse } from '../schemas/transaction.schemas';

type CheckoutSessionTransactionLink = NonNullable<
  GetTransactionDetailResponse['checkoutSessionTransactions']
>[number];

type CheckoutCartPanelProps = {
  checkoutCart: NonNullable<
    NonNullable<GetTransactionDetailResponse['metadata']>['checkoutCart']
  >;
  currentTransactionId: string;
  checkoutSessionTransactions?: CheckoutSessionTransactionLink[];
};

type CartLine = CheckoutCartPanelProps['checkoutCart']['lines'][number];

type DisplayRow = CartLine & {
  displayLine: number;
  copyIndex: number;
  copiesInLine: number;
  displayQty: number;
};

const formatHostApplication = (
  hostApplication?: {
    displayName?: string;
    version?: string;
  } | null,
) => {
  if (!hostApplication?.displayName) return '—';
  return hostApplication.version
    ? `${hostApplication.displayName} ${hostApplication.version}`
    : hostApplication.displayName;
};

const expandCartLines = (lines: CartLine[]): DisplayRow[] => {
  let displayLine = 0;

  return lines.flatMap((line) => {
    const copiesInLine =
      line.category === 'Subscription' && line.quantity > 1 ? line.quantity : 1;

    return Array.from({ length: copiesInLine }, (_, copyIndex) => {
      displayLine += 1;
      return {
        ...line,
        displayLine,
        copyIndex,
        copiesInLine,
        displayQty: copiesInLine > 1 ? 1 : line.quantity,
      };
    });
  });
};

const CheckoutCartPanel = ({
  checkoutCart,
  currentTransactionId,
  checkoutSessionTransactions = [],
}: CheckoutCartPanelProps) => {
  const theme = useTheme();
  const { lines, thisTransaction } = checkoutCart;
  const displayRows = expandCartLines(lines);
  const highlightBg = alpha(
    theme.palette.info.main,
    theme.palette.mode === 'light' ? 0.08 : 0.16,
  );

  const transactionIdByPosition = new Map(
    checkoutSessionTransactions.map((entry) => [
      `${entry.cartLineIndex}:${entry.copyIndex}`,
      entry.transactionId,
    ]),
  );

  const isHighlightedRow = (row: DisplayRow) =>
    row.cartLineIndex === thisTransaction.cartLineIndex &&
    row.copyIndex === thisTransaction.copyIndex;

  const resolveRowTransactionId = (row: DisplayRow) =>
    transactionIdByPosition.get(`${row.cartLineIndex}:${row.copyIndex}`);

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Checkout cart
      </Typography>

      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        <Box
          aria-hidden
          sx={{
            width: 14,
            height: 14,
            borderRadius: 0.5,
            backgroundColor: highlightBg,
            border: `1px solid ${alpha(theme.palette.info.main, 0.35)}`,
            flexShrink: 0,
          }}
        />
        <Typography variant="body2" color="text.secondary">
          Highlighted row is this transaction
        </Typography>
      </Stack>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell width={72}>Line</TableCell>
            <TableCell>Product</TableCell>
            <TableCell>Category</TableCell>
            <TableCell>Host app</TableCell>
            <TableCell align="right">Qty</TableCell>
            <TableCell align="right" width={56} />
          </TableRow>
        </TableHead>
        <TableBody>
          {displayRows.map((row) => {
            const rowTransactionId = resolveRowTransactionId(row);
            const showViewLink =
              !isHighlightedRow(row) &&
              Boolean(rowTransactionId) &&
              rowTransactionId !== currentTransactionId;

            return (
              <TableRow
                key={`${row.productId}-${row.cartLineIndex}-${row.copyIndex}`}
                sx={
                  isHighlightedRow(row)
                    ? { backgroundColor: highlightBg }
                    : undefined
                }
              >
                <TableCell>{row.displayLine}</TableCell>
                <TableCell>{row.productName}</TableCell>
                <TableCell>{row.category}</TableCell>
                <TableCell>
                  {formatHostApplication(row.hostApplication)}
                </TableCell>
                <TableCell align="right">{row.displayQty}</TableCell>
                <TableCell align="right">
                  {showViewLink && rowTransactionId ? (
                    <Tooltip title="View transaction" arrow>
                      <IconButton
                        component={Link}
                        to={`/transactions/${rowTransactionId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        size="small"
                        aria-label="View transaction"
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ) : null}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
};

export default CheckoutCartPanel;
